import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BookingInquiryPayload,
  BookingProfileImage,
  BookingsSetUpPayload,
  ConfirmBookingPayload,
  CreateBookingResponse,
  FetchedCalenderInterface,
  GetBookingsParams,
  GetBookingsResponse,
  GetMyBookingsParams,
  GetMyBookingsResponse,
  GetSpeakingInvitationsParams,
  SpeakingInvitationsResponse,
  SpeakingInvitationDetails,
  InviteToSpeakPayload,
  SendCorrespondence,
  RespondToInvitation,
  Slot,
} from "interfaces";
import { ProjectorSetting } from "interfaces/interfaces-data";
import {
  getBookingCalendarsService,
  getMyBookingsService,
  createBookingCalendarService,
  confirmBookingService,
  cancelBookingSlotService,
  sendMeetingReminderService,
  inviteToSpeakService,
  getSpeakingInvitationService,
  getSpeakingInvitationDetailsService,
  getSpeakingCorrespondenceService,
  sendCorrespondenceService,
  respondToInvitationService,
  createBookingService,
  getPublicBookingCalendarService,
  getPublicBookingCalendarsService,
  getPublicBookingPageService,
  getPublicBookingProfileImageService,
  sendBookingInquiryService,
} from "services";

// Query Hooks (GET requests)
export const useGetBookingCalendars = (
  params: GetBookingsParams = {},
  enabled = true,
) => {
  return useQuery<GetBookingsResponse>({
    queryKey: ["bookingCalendars", params],
    queryFn: () => getBookingCalendarsService(params),
    enabled,
  });
};

export const useGetMyBookings = (params: GetMyBookingsParams = {}) => {
  return useQuery<GetMyBookingsResponse>({
    queryKey: ["myBookings", params],
    queryFn: () => getMyBookingsService(params),
  });
};

export const useGetPublicBookingCalendars = (
  username: string,
  pageLimit = 100,
) => {
  return useInfiniteQuery({
    queryKey: ["publicBookingCalendars", username, pageLimit],
    queryFn: ({ pageParam }) =>
      getPublicBookingCalendarsService({
        username,
        page: pageParam,
        pageLimit,
        range: 0,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.nextPage ?? undefined,
    enabled: !!username,
  });
};

export const useGetPublicBookingCalendar = (
  username: string,
  bookingId?: string,
) => {
  return useQuery<FetchedCalenderInterface>({
    queryKey: ["publicBookingCalendar", username, bookingId],
    queryFn: () => getPublicBookingCalendarService(username, bookingId!),
    enabled: !!username && !!bookingId,
  });
};

export const useGetPublicBookingPage = (username: string) => {
  return useQuery<ProjectorSetting>({
    queryKey: ["publicBookingPage", username],
    queryFn: () => getPublicBookingPageService(username),
    enabled: !!username,
    retry: false,
  });
};

export const useGetPublicBookingProfileImage = (username: string) => {
  return useQuery<BookingProfileImage | null>({
    queryKey: ["publicBookingProfileImage", username],
    queryFn: () => getPublicBookingProfileImageService(username),
    enabled: !!username,
    retry: false,
  });
};

// Mutation Hooks (POST requests)
export const useCreateBookingCalendar = () => {
  return useMutation<string, Error, BookingsSetUpPayload>({
    mutationFn: (bookingData: BookingsSetUpPayload) =>
      createBookingCalendarService(bookingData),
  });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateBookingResponse, Error, Slot>({
    mutationFn: (booking) => createBookingService(booking),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["publicBookingCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["publicBookingCalendars"] });
    },
  });
};

export const useSendBookingInquiry = () => {
  return useMutation<void, Error, BookingInquiryPayload>({
    mutationFn: (inquiry) => sendBookingInquiryService(inquiry),
  });
};

export const useConfirmBooking = () => {
  return useMutation<
    void,
    Error,
    { bookingId: string } & ConfirmBookingPayload
  >({
    mutationFn: ({ bookingId, ...payload }) =>
      confirmBookingService(bookingId, payload),
  });
};

export const useCancelBookingSlot = () => {
  return useMutation<void, Error, string>({
    mutationFn: (slotId: string) => cancelBookingSlotService(slotId),
  });
};

export const useSendBookingSlotReminder = () => {
  return useMutation<void, Error, string>({
    mutationFn: (slotId: string) => sendMeetingReminderService(slotId),
  });
};

export const useInviteToSpeak = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { invitationId: string },
    Error,
    { speakerId: string } & InviteToSpeakPayload
  >(
    {
      mutationFn: ({ speakerId, ...payload }) =>
        inviteToSpeakService(speakerId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["speaking-invitations"] });
      },
    },
  );
};

export const useGetInvitationToSpeak = (
  params: GetSpeakingInvitationsParams = {},
) => {
  return useQuery<SpeakingInvitationsResponse>({
    queryKey: ["speaking-invitations", params],
    queryFn: () => getSpeakingInvitationService(params),
  });
};

export const useGetSpeakingInvitationDetails = (id: string) => {
  return useQuery<SpeakingInvitationDetails>({
    queryKey: ["speaking-invitation", id],
    queryFn: () => getSpeakingInvitationDetailsService(id),
    enabled: !!id,
  });
};

export const useGetSpeakingCorrespondence = (
  id: string,
  refetchInterval: number | false = false,
) => {
  return useQuery({
    queryKey: ["speaking-correspondence", id],
    queryFn: () => getSpeakingCorrespondenceService(id),
    enabled: !!id,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
};

export const useSendCorrespondence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendCorrespondence) =>
      sendCorrespondenceService(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["speaking-correspondence", variables.id],
      });
    },
  });
};

export const useRespondToInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RespondToInvitation) => {
      return respondToInvitationService(payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["speaking-invitations"] });
      queryClient.invalidateQueries({
        queryKey: ["speaking-invitation", variables.id],
      });
    },
  });
};
